import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { aircraftLabel, useAircraft } from '../aircraft/AircraftContext';
import { useFeedback } from '../hooks/useFeedback';
import { useSupportUnread } from '../hooks/useSupportUnread';
import { getApiErrorMessage } from '../utils/apiError';
import './Navbar.css';

const SupportBadge = ({ count, label }: { count: number; label: string }) =>
  count > 0 ? (
    <span className="nav-badge">
      <span className="nav-badge-label">{label}: </span>
      {count > 99 ? '99+' : count}
    </span>
  ) : null;

const SupportNavbar = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const waiting = useSupportUnread(true);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <nav className="navbar">
      <div className="navbar-row">
        <div className="navbar-brand">
          <Link to="/support/inbox" className="brand-link">
            <span>БортЖурнал</span>
          </Link>
          <span className="brand-aircraft">Поддержка</span>
        </div>
        <div className="navbar-links">
          <div className="nav-group nav-group-account">
            <Link to="/support/inbox">
              <img src="/support.png" alt="" className="nav-icon" />
              Входящие
              <SupportBadge count={waiting} label="Ждут ответа" />
            </Link>
            <button type="button" className="logout-btn" onClick={handleLogout}>
              <img src="/exit.png" alt="" className="nav-icon" />
              Выйти
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const Navbar = () => {
  const { isSupport } = useAuth();
  return isSupport ? <SupportNavbar /> : <ClubNavbar />;
};

const ClubNavbar = () => {
  // desktop/mobile club selects must not share the same id
  const [menuOpen, setMenuOpen] = useState(false);
  const [clubSwitching, setClubSwitching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, feedbackNode } = useFeedback();
  const { logout, clubs, switchClub, user, canFlyOrBook, canUseProfile } =
    useAuth();
  const supportUnread = useSupportUnread(true);
  const supportState = location.pathname.startsWith('/support')
    ? location.state
    : { from: location.pathname };
  const {
    aircraftList,
    aircraftId,
    current,
    loading,
    setAircraftId,
    refreshList,
  } = useAircraft();

  const hasAircraft = aircraftList.length > 0;
  const canBrowse = !loading && hasAircraft;
  const activeClubId = user?.club_id ?? clubs.find((c) => c.active)?.club_id ?? 0;
  const homeTo = canBrowse ? '/' : '/club';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAircraftChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    if (!Number.isFinite(id)) return;
    setAircraftId(id);
    window.dispatchEvent(new Event('aircraft-updated'));
  };

  const handleClubChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    if (!Number.isFinite(id) || id === activeClubId) return;
    setClubSwitching(true);
    try {
      await switchClub(id);
      navigate('/club');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось сменить клуб'));
    } finally {
      setClubSwitching(false);
    }
  };

  const label = current ? aircraftLabel(current) : '';
  const renderClubSwitcher = (fieldId: string) =>
    clubs.length > 1 ? (
      <select
        id={fieldId}
        name={fieldId}
        className="brand-aircraft-select"
        value={activeClubId || ''}
        disabled={clubSwitching}
        onChange={handleClubChange}
        aria-label="Выбор клуба"
        title="Клуб"
      >
        {clubs.map((c) => (
          <option key={c.club_id} value={c.club_id}>
            {c.club_name}
          </option>
        ))}
      </select>
    ) : user?.club_name ? (
      <span className="brand-aircraft" title="Клуб">
        {user.club_name}
      </span>
    ) : null;
  const desktopClubSwitcher = renderClubSwitcher('navbar-club-select');
  const mobileClubSwitcher = renderClubSwitcher('navbar-club-select-mobile');

  return (
    <nav className="navbar">
      {feedbackNode}
      <div className="navbar-row">
        <div className="navbar-brand">
          <Link to={homeTo} className="brand-link">
            <span>БортЖурнал</span>
          </Link>
          <div className="navbar-club nav-desktop-inline">{desktopClubSwitcher}</div>
          {canBrowse && aircraftList.length > 1 ? (
            <select
              id="navbar-aircraft-select"
              name="aircraft_id"
              className="brand-aircraft-select nav-desktop-only"
              value={aircraftId}
              onChange={handleAircraftChange}
              onFocus={() => void refreshList()}
              aria-label="Выбор борта"
            >
              {aircraftList.map((item) => (
                <option key={item.id} value={item.id}>
                  {aircraftLabel(item)}
                </option>
              ))}
            </select>
          ) : canBrowse && label ? (
            <Link to="/aircraft" className="brand-aircraft nav-desktop-only">
              {label}
            </Link>
          ) : null}
        </div>

        <div className="navbar-links">
          <div className="nav-group nav-group-home">
            <Link to={homeTo}>
              <img src="/home.png" alt="Главная" className="nav-icon" />
              Главная
            </Link>
          </div>

          {canBrowse ? (
            <div className="nav-group nav-group-main nav-desktop-only">
              <div className="nav-main-row">
                {canFlyOrBook ? (
                  <Link to="/flights">
                    <img src="/plus.png" alt="Полёты" className="nav-icon" />
                    Добавление полёта
                  </Link>
                ) : null}
                <Link to="/stats">
                  <img src="/stats.png" alt="Статистика" className="nav-icon" />
                  Статистика
                </Link>
              </div>
              <div className="nav-main-row">
                {canFlyOrBook ? (
                  <Link to="/bookings">
                    <img src="/bookings.png" alt="Брони" className="nav-icon" />
                    Брони
                  </Link>
                ) : null}
                <Link to="/issues">
                  <img
                    src="/issues.png"
                    alt="Неисправности"
                    className="nav-icon"
                  />
                  Неисправности
                </Link>
                <Link to="/maintenance">
                  <img src="/history.png" alt="ТО" className="nav-icon" />
                  ТО
                </Link>
              </div>
            </div>
          ) : null}

          <div className="nav-group nav-group-account">
            <Link to="/aircraft" className="nav-desktop-only">
              <img src="/plane.png" alt="Самолёт" className="nav-icon" />
              Самолёт
            </Link>
            <Link to="/club" className="nav-desktop-only">
              <img src="/star.png" alt="Клуб" className="nav-icon" />
              Клуб
            </Link>
            {canUseProfile ? (
              <Link to="/profile" className="nav-desktop-only">
                <img src="/me.png" alt="Я" className="nav-icon" />
                Личный кабинет
              </Link>
            ) : null}
            <Link to="/support" state={supportState}>
              <img src="/support.png" alt="" className="nav-icon" />
              Поддержка
              <SupportBadge count={supportUnread} label="Новых ответов" />
            </Link>
            <button type="button" className="logout-btn" onClick={handleLogout}>
              <img src="/exit.png" alt="Выйти" className="nav-icon" />
              Выйти
            </button>
            <button
              type="button"
              className="navbar-menu-toggle"
              aria-expanded={menuOpen}
              aria-label="Меню"
              onClick={() => setMenuOpen((open) => !open)}
            >
              Меню
            </button>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div className="navbar-mobile-menu" role="navigation">
          {canBrowse && aircraftList.length > 1 ? (
            <select
              className="brand-aircraft-select"
              value={aircraftId}
              onChange={handleAircraftChange}
              aria-label="Выбор борта"
            >
              {aircraftList.map((item) => (
                <option key={item.id} value={item.id}>
                  {aircraftLabel(item)}
                </option>
              ))}
            </select>
          ) : null}
          {canBrowse && canFlyOrBook ? <Link to="/flights" onClick={() => setMenuOpen(false)}>Полёты</Link> : null}
          {canBrowse ? <Link to="/stats" onClick={() => setMenuOpen(false)}>Статистика</Link> : null}
          {canBrowse && canFlyOrBook ? <Link to="/bookings" onClick={() => setMenuOpen(false)}>Брони</Link> : null}
          {canBrowse ? <Link to="/issues" onClick={() => setMenuOpen(false)}>Неисправности</Link> : null}
          {canBrowse ? <Link to="/maintenance" onClick={() => setMenuOpen(false)}>ТО</Link> : null}
          <Link to="/aircraft" onClick={() => setMenuOpen(false)}>Самолёт</Link>
          <Link to="/club" onClick={() => setMenuOpen(false)}>Клуб</Link>
          {canUseProfile ? (
            <Link to="/profile" onClick={() => setMenuOpen(false)}>Личный кабинет</Link>
          ) : null}
        </div>
      ) : null}

      {mobileClubSwitcher ? (
        <div className="navbar-club-mobile">{mobileClubSwitcher}</div>
      ) : null}
    </nav>
  );
};

export default Navbar;
